import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PressSection = () => {
  const pressFeatures = [
    {
      publication: "TechCrunch",
      title: "Giftsy Modern Raises $15M to Revolutionize Gift-Giving with AI",
      excerpt: "The platform's unique approach to relationship-centered commerce is changing how people think about meaningful gift-giving.",
      date: "March 2024",
      logo: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=100&fit=crop",
      category: "Funding"
    },
    {
      publication: "Forbes",
      title: "The Future of E-commerce is Emotional Intelligence",
      excerpt: "Giftsy Modern demonstrates how AI can enhance human connection rather than replace it, setting new standards for personalized commerce.",
      date: "February 2024",
      logo: "https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=200&h=100&fit=crop",
      category: "Innovation"
    },
    {
      publication: "Wired",
      title: "How AI is Making Gift-Giving More Thoughtful, Not Less",
      excerpt: "A deep dive into the technology and psychology behind Giftsy Modern's recommendation engine and its impact on relationships.",
      date: "January 2024",
      logo: "https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=200&h=100&fit=crop",
      category: "Technology"
    },
    {
      publication: "Harvard Business Review",
      title: "The Psychology of Digital Gift-Giving",
      excerpt: "Research shows how platforms like Giftsy Modern are strengthening relationships through technology-assisted thoughtfulness.",
      date: "December 2023",
      logo: "https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=200&h=100&fit=crop",
      category: "Research"
    }
  ];

  const awards = [
    {
      title: "Best AI Innovation",
      organization: "Tech Innovation Awards 2024",
      icon: "Award",
      color: "primary"
    },
    {
      title: "Top 10 Startups to Watch",
      organization: "Forbes 30 Under 30",
      icon: "Star",
      color: "secondary"
    },
    {
      title: "Excellence in User Experience",
      organization: "UX Design Awards",
      icon: "Heart",
      color: "accent"
    },
    {
      title: "Social Impact Recognition",
      organization: "B Corp Certification",
      icon: "Users",
      color: "success"
    }
  ];

  const testimonials = [
    {
      quote: "Giftsy Modern represents the future of commerce - where technology serves human connection rather than replacing it.",
      author: "Dr. Sarah Mitchell",
      role: "Relationship Psychology Expert",
      organization: "Stanford University"
    },
    {
      quote: "The platform's approach to cultural sensitivity in gift-giving sets a new standard for global e-commerce platforms.",
      author: "Prof. James Chen",
      role: "Cultural Anthropologist",
      organization: "MIT"
    },
    {
      quote: "Their AI ethics framework should be a model for how technology companies approach personalization and privacy.",
      author: "Maria Rodriguez",
      role: "Tech Ethics Researcher",
      organization: "Oxford Internet Institute"
    }
  ];

  const getCategoryColor = (category) => {
    const colorMap = {
      'Funding': 'bg-primary-100 text-primary-700',
      'Innovation': 'bg-secondary-100 text-secondary-700',
      'Technology': 'bg-accent-100 text-accent-700',
      'Research': 'bg-success-100 text-success-700'
    };
    return colorMap[category] || 'bg-accent-100 text-accent-700';
  };

  const getAwardColor = (color) => {
    const colorMap = {
      primary: "from-primary-400 to-primary-600",
      secondary: "from-secondary-400 to-secondary-600",
      accent: "from-accent-400 to-accent-600",
      success: "from-success-400 to-success-600"
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl lg:text-5xl font-bold text-accent-800 mb-6">
            Press & Recognition
          </h2>
          <p className="text-lg lg:text-xl text-accent-600 max-w-3xl mx-auto leading-relaxed">
            Industry recognition and media coverage highlighting our impact on gift-giving culture and relationship technology.
          </p>
        </div>

        {/* Press Features */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {pressFeatures.map((feature, index) => (
            <div key={index} className="group hover-lift">
              <div className="bg-white rounded-2xl p-6 shadow-md border border-accent-100 h-full transition-all duration-300 group-hover:shadow-lg group-hover:border-primary-200">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-16 h-8 rounded overflow-hidden flex-shrink-0">
                    <Image
                      src={feature.logo}
                      alt={`${feature.publication} logo`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-medium text-accent-800">{feature.publication}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(feature.category)}`}>
                        {feature.category}
                      </span>
                    </div>
                    <p className="text-sm text-accent-600">{feature.date}</p>
                  </div>
                </div>
                
                <h3 className="font-heading text-lg font-bold text-accent-800 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                  {feature.title}
                </h3>
                
                <p className="text-accent-600 leading-relaxed mb-4">
                  {feature.excerpt}
                </p>
                
                <div className="flex items-center text-primary-600 text-sm font-medium">
                  <span>Read Article</span>
                  <Icon name="ExternalLink" size={16} className="ml-2" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Awards Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="font-heading text-2xl lg:text-3xl font-bold text-accent-800 mb-4">
              Awards & Recognition
            </h3>
            <p className="text-lg text-accent-600 max-w-2xl mx-auto">
              Industry recognition for innovation, design, and social impact
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {awards.map((award, index) => (
              <div key={index} className="text-center group hover-lift">
                <div className="bg-gradient-warm rounded-2xl p-6 border border-primary-100 h-full transition-all duration-300 group-hover:shadow-lg group-hover:border-primary-200">
                  <div className={`w-16 h-16 bg-gradient-to-br ${getAwardColor(award.color)} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon name={award.icon} size={28} className="text-white" />
                  </div>
                  
                  <h4 className="font-heading text-lg font-bold text-accent-800 mb-2">
                    {award.title}
                  </h4>
                  
                  <p className="text-accent-600 text-sm">
                    {award.organization}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Expert Testimonials */}
        <div className="bg-gradient-warm rounded-2xl p-8 lg:p-12 border border-primary-100">
          <div className="text-center mb-12">
            <h3 className="font-heading text-2xl lg:text-3xl font-bold text-accent-800 mb-4">
              Expert Testimonials
            </h3>
            <p className="text-lg text-accent-600">
              What industry experts and researchers say about our approach
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md border border-accent-100">
                <div className="mb-4">
                  <Icon name="Quote" size={24} className="text-primary-400" />
                </div>
                
                <p className="text-accent-600 leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                
                <div className="border-t border-accent-100 pt-4">
                  <p className="font-medium text-accent-800 mb-1">
                    {testimonial.author}
                  </p>
                  <p className="text-sm text-accent-600 mb-1">
                    {testimonial.role}
                  </p>
                  <p className="text-sm text-primary-600 font-medium">
                    {testimonial.organization}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Media Kit */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-accent-100 inline-block">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <Icon name="Download" size={24} className="text-primary-600" />
              </div>
              <div className="text-left">
                <h4 className="font-heading text-lg font-bold text-accent-800">
                  Media Kit
                </h4>
                <p className="text-accent-600 text-sm">
                  Logos, images, and brand assets for press use
                </p>
              </div>
            </div>
            <div className="flex space-x-4">
              <button className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-600 transition-colors duration-200">
                Download Kit
              </button>
              <button className="px-4 py-2 border border-accent-300 text-accent-600 rounded-lg font-medium hover:bg-accent-50 transition-colors duration-200">
                Contact Press
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PressSection;